import { useOrganization } from 'core/stores/hooks/useOrganization';
import {
  CandidateBatchStatus,
  useGetCandidateBatchByStatusAndAtsJobLazyQuery,
  useUpdateCandidateBatchMutation
} from 'generated-graphql/hooks/apollo-hooks';
import React, { useState } from 'react';
import { AtsJobInput } from 'services/candidate/types';
import { KomboJobTable } from 'services/integration/components/KomboJobTable';
import { ToastType, useToast } from 'services/toast';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { extractListItems } from 'core/utils/apollo/extractListItems';

type Props = {
  atsJobs: AtsJobInput[];
  onNext: (atsJobs: AtsJobInput[]) => void;
};

export const ATSSelectJobStep: React.FC<Props> = ({
  atsJobs: initialAtsJobs,
  onNext
}) => {
  const { showToast } = useToast();
  const [atsJobs, setAtsJobs] = useState<AtsJobInput[]>(initialAtsJobs);
  const organization = useOrganization();
  const navigate = useNavigate();

  const [updateCandidateBatch, { loading: isUpdatingDraft }] =
    useUpdateCandidateBatchMutation();

  const [getCandidateBatchByStatusAndAtsJob, { loading }] =
    useGetCandidateBatchByStatusAndAtsJobLazyQuery({
      variables: {
        atsJobId: atsJobs[0]?.atsJobId,
        status: CandidateBatchStatus.Draft,
        limit: 10,
        filter: {
          organizationId: {
            eq: organization?.id
          }
        }
      }
    });

  const handleNext = async () => {
    if (!atsJobs.length) {
      showToast({
        message: 'Please select at least one job',
        type: ToastType.Error
      });
      return;
    }

    const { data } = await getCandidateBatchByStatusAndAtsJob();

    const draftCandidateBatch = extractListItems(
      data?.getCandidateBatchByStatusAndAtsJobId?.items || []
    )[0];

    // Redirect to existing draft analysis if it exists to not create multiple live analyses for the same job.
    // Persist the freshly-typed job description onto the draft first — otherwise the
    // remount discards it and the requirements step falls back to the draft's stored value.
    if (draftCandidateBatch?.id) {
      try {
        await updateCandidateBatch({
          variables: {
            input: {
              id: draftCandidateBatch.id,
              jobDescription: atsJobs[0].jobDescription
            }
          }
        });
      } catch {
        showToast({
          message:
            'We couldn’t save your job description. Try again or contact support.',
          type: ToastType.Error
        });
        return;
      }
      navigate(`${Paths.ANALYSE_CREATE}/${draftCandidateBatch.id}`);
      return;
    }

    onNext(atsJobs);
  };

  return (
    <KomboJobTable
      setAtsJobs={setAtsJobs}
      handleNext={handleNext}
      disableNext={atsJobs.length === 0}
      disableAddJobDescription
      onAddJobDescription={() => {
        //
      }}
      isNextLoading={loading || isUpdatingDraft}
      isForAnalysis
    />
  );
};
